import type { LucideIcon } from 'lucide-react'
import { useEffect, useRef } from 'react'

import { cn } from '@/utils/cn'

export interface TabItem {
  id: string
  label: string
  icon?: LucideIcon
  /** Aba visível mas não selecionável (feature ainda não disponível). */
  disabled?: boolean
}

interface TabsProps {
  tabs: TabItem[]
  value: string
  onValueChange: (id: string) => void
  className?: string
}

/**
 * Abas com sublinhado (estilo do mock de detalhes do colaborador).
 * Abaixo de `lg` ficam numa linha só com rolagem horizontal (barra oculta);
 * no desktop quebram para a linha de baixo quando não cabem.
 */
export function Tabs({ tabs, value, onValueChange, className }: TabsProps) {
  const listRef = useRef<HTMLDivElement>(null)

  // Mantém a aba ativa visível na rolagem horizontal (ex.: aberta via `?aba=` na URL).
  // Ajusta só o `scrollLeft` da lista — `scrollIntoView` também rolaria a página.
  useEffect(() => {
    const list = listRef.current
    const active = list?.querySelector<HTMLElement>('[aria-selected="true"]')
    if (!list || !active || list.scrollWidth <= list.clientWidth) return
    list.scrollLeft = active.offsetLeft - (list.clientWidth - active.offsetWidth) / 2
  }, [value])

  return (
    <div className={cn('border-b border-line', className)} role="tablist">
      <div
        ref={listRef}
        className="pc-no-scrollbar relative flex gap-x-1 gap-y-0.5 overflow-x-auto lg:flex-wrap lg:overflow-visible"
      >
        {tabs.map((tab) => {
          const active = tab.id === value
          const Icon = tab.icon
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={active}
              aria-disabled={tab.disabled || undefined}
              disabled={tab.disabled}
              onClick={() => !tab.disabled && onValueChange(tab.id)}
              className={cn(
                'inline-flex shrink-0 items-center gap-2 whitespace-nowrap border-b-2 px-3 py-2.5 text-sm font-medium transition-colors',
                tab.disabled
                  ? 'cursor-not-allowed border-transparent text-fg-subtle/60'
                  : active
                    ? 'border-brand text-brand'
                    : 'border-transparent text-fg-muted hover:text-fg',
              )}
            >
              {Icon && <Icon className="size-4" />}
              {tab.label}
              {tab.disabled && (
                <span className="rounded-full bg-surface-2 px-1.5 py-0.5 text-[0.6rem] font-medium uppercase tracking-wide text-fg-subtle">
                  em breve
                </span>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}
